import React from "react";
import styled, { createGlobalStyle } from "styled-components";

import Viewer from "../../UI/Viewer";
import ChapterBar from "../ChapterBar/ChapterBar";
import SwipableChapterLayout from "./SwipableChapterLayout";
import StandardChapterLayout from "./StandardChapterLayout";
import { isMobileDevice } from "../../../utils/mobile";
import { withAudio, AudioCtx } from "../ChapterAudio/ChapterAudio";
import { withChapter, ChapterCtx } from "../ChapterContainer";
import { ChapterFooter } from "./ChapterFooter";
import { useChapterMode, CHAPTER_MODES } from "./ChapterMode";
import { ChapterScroller } from "./ChapterScroller";
import { FooterContainer } from "../../Footer/Footer";
import { useScriptureRouting } from "components/Bible/useScriptureRouting";
import { useParallelManager } from "../utils";

// NOTE: this isn't an ideal solution, but since the site footer is not nested within
// the chapter layout, and the audio provider is not within the SiteWrapper, this is the
// only means we have to communicate to the SiteFooter that it should have more padding at the bottom
// while the audio play is present.
// TODO: provide a more permanant solution when the new audio player is implemented
const SiteFooterAudioPadding = createGlobalStyle`
  ${FooterContainer} {
    margin-bottom: ${props => (props.hasAudio ? "120px" : "0")};
  }
`;

const ChapterViewer = styled(Viewer)<{
  numOfParallels: number;
}>`
  padding-top: 0;
`;

interface ChapterLayoutProps {
  chapter: ChapterCtx;
  audio: AudioCtx;
}

const ChapterLayout: React.FC<ChapterLayoutProps> = ({ chapter, audio }) => {
  useParallelManager();

  const { parallelBibleIds } = useScriptureRouting();

  const isMobile = isMobileDevice();
  const platformType = isMobile ? "phone" : "desktop";
  const numOfParallels = parallelBibleIds.length;

  const Layout = isMobile ? SwipableChapterLayout : StandardChapterLayout;
  const layoutPlatform = isMobile ? platformType : "desktop";

  const {
    mode,
    setModeAudioSync,
    setModeDefault,
    decrementScrollLocks,
    incrementScrollLocks
  } = useChapterMode(numOfParallels > 0);

  return (
    <ChapterViewer
      numOfParallels={numOfParallels}
      wide={parallelBibleIds.length > 0}
    >
      <div
        style={{
          opacity:
            isMobile ||
            mode === CHAPTER_MODES.DEFAULT ||
            mode === CHAPTER_MODES.AUDIO_USER_SCROLLING
              ? 1
              : 0
        }}
      >
        <ChapterBar setModeDefault={setModeDefault} />
      </div>
      <Layout
        platformType={layoutPlatform}
        setModeDefault={setModeDefault}
      ></Layout>
      <ChapterFooter
        audioIsPlayable={audio.isPlayable}
        mode={mode}
        setModeDefault={setModeDefault}
        setModeAudioSync={setModeAudioSync}
      />
      <ChapterScroller
        mode={mode}
        currentAudioVerseElement={audio.currentAudioVerseElement}
        currentAudioVerseId={audio.currentAudioVerseId}
        incrementScrollLocks={incrementScrollLocks}
        decrementScrollLocks={decrementScrollLocks}
      />
      <SiteFooterAudioPadding hasAudio={audio.isPlayable} />
    </ChapterViewer>
  );
};

export default withChapter(withAudio(ChapterLayout));
